import React from "react";
import { DashTransactionsResponse } from "../ceo-dashboard.service";
import CustomTable from "src/components/custom-table/custom-table";
import { ColumnsType } from "antd/es/table";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

interface FundSubscriptionsTableProps {
  transactionsData: DashTransactionsResponse | null;
  loading: boolean;
  dateRange: [Dayjs | null, Dayjs | null];
}

const FundSubscriptionsTable: React.FC<FundSubscriptionsTableProps> = ({
  transactionsData,
  loading,
  dateRange,
}) => {
  const handleTableChange = () => {
    // Table change handler - not used for this table but required by CustomTable
  };

  // Default funds
  const defaultFunds = [
    "Kamco Investment Fund",
    "Kamco Islamic Fund",
    "Kamco Premier Market Index Fund"
  ];

  // Generate all months in the selected range
  const generateMonthsRange = () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return [];

    const months: string[] = [];
    let current = startDate.clone();

    while (current.isSameOrBefore(endDate, 'month')) {
      months.push(current.format('MMM YYYY'));
      current = current.add(1, 'month');
    }

    return months;
  };

  // Process data for the table using subscriptionBreakdown
  const processTableData = () => {
    if (!transactionsData?.data?.subscriptionBreakdown) return [];

    const months = generateMonthsRange();
    const groupedData: { [key: string]: { [key: string]: number } } = {};

    // Initialize months and funds with zero values
    months.forEach(month => {
      groupedData[month] = {};
      defaultFunds.forEach(fund => {
        groupedData[month][fund] = 0;
      });
    });

    // Process subscriptionBreakdown data
    transactionsData.data.subscriptionBreakdown.forEach(breakdown => {
      // Parse the month string (format: "2025-08")
      const breakdownDate = dayjs(breakdown.month + "-01"); // Add day to make it a valid date
      const chartMonth = breakdownDate.format('MMM YYYY');
      const amount = breakdown.total;
      const fundName = breakdown.fundNameEn;

      if (months.includes(chartMonth)) {
        // Find matching fund with improved matching logic
        const matchingFund = defaultFunds.find(fund => {
          const fundNameNormalized = fund.toLowerCase().trim();
          const breakdownNameNormalized = fundName.toLowerCase().trim();

          return (
            // Exact match
            fundNameNormalized === breakdownNameNormalized ||
            // Contains match (either direction)
            fundNameNormalized.includes(breakdownNameNormalized) ||
            breakdownNameNormalized.includes(fundNameNormalized) ||
            // Match key words (Islamic, Investment, Premier)
            (fundNameNormalized.includes('islamic') && breakdownNameNormalized.includes('islamic')) ||
            (fundNameNormalized.includes('investment') && breakdownNameNormalized.includes('investment')) ||
            (fundNameNormalized.includes('premier') && breakdownNameNormalized.includes('premier'))
          );
        });

        if (matchingFund) {
          groupedData[chartMonth][matchingFund] += amount;
        }
      }
    });

    // Transform data for table display
    return defaultFunds.map(fund => {
      const rowData: any = { fund };
      let total = 0;

      months.forEach(month => {
        const value = groupedData[month][fund];
        rowData[month] = value;
        total += value;
      });

      rowData['Total'] = total;
      return rowData;
    });
  };

  // Generate dynamic columns
  const generateColumns = (): ColumnsType<any> => {
    const months = generateMonthsRange();
    const monthColumnWidth = Math.floor(75 / months.length); // 75% divided by number of months

    const columns: ColumnsType<any> = [
      {
        title: "Funds",
        dataIndex: "fund",
        key: "fund",
        width: "15%",
        render: (fund: string) => {
          // Get short name for display
          let shortName = fund;
          if (fund.includes("Investment")) shortName = "Investment Fund";
          else if (fund.includes("Islamic")) shortName = "Islamic Fund";
          else if (fund.includes("Premier")) shortName = "Premier Fund";

          return (
            <div className="text-zinc-800 text-xs font-semibold font-['Montserrat']">
              {shortName}
            </div>
          );
        },
      },
    ];

    // Add month columns
    months.forEach(month => {
      columns.push({
        title: month,
        dataIndex: month,
        key: month,
        width: `${monthColumnWidth}%`,
        render: (amount: number) => (
          <div className="text-zinc-800 text-xs font-medium font-['Montserrat']">
            {amount.toLocaleString()} KWD
          </div>
        ),
      });
    });

    // Add Total column
    columns.push({
      title: "Total",
      dataIndex: "Total",
      key: "Total",
      width: "10%",
      render: (total: number) => (
        <div className="text-zinc-800 text-xs font-bold font-['Montserrat'] bg-slate-50 px-2 py-1 rounded">
          {total.toLocaleString()} KWD
        </div>
      ),
    });

    return columns;
  };

  const tableData = processTableData();
  const columns = generateColumns();

  return (
    <div className="w-full flex flex-col justify-start items-start shadow-md bg-white rounded-lg gap-5 py-5">
      <div className="w-full inline-flex justify-start items-center gap-3.5 px-5">
        <div className="flex-1 justify-start text-zinc-800 text-sm font-semibold font-['Montserrat']">
          Monthly Fund Subscriptions
        </div>
      </div>

      <div className="w-full px-5 bg-white [&_.ant-table]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:bg-slate-200 [&_.ant-table-thead>tr>th]:border-b [&_.ant-table-thead>tr>th]:border-gray-300 [&_.ant-table-thead>tr>th]:text-sky-900 [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-thead>tr>th]:font-semibold [&_.ant-table-thead>tr>th]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:px-5 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-tbody>tr>td]:border-b [&_.ant-table-tbody>tr>td]:border-zinc-300 [&_.ant-table-tbody>tr>td]:px-5 [&_.ant-table-tbody>tr>td]:py-4 [&_.ant-table-tbody>tr>td]:bg-white [&_.ant-table-tbody>tr:hover>td]:bg-slate-50 [&_.ant-pagination]:mx-5 [&_.ant-pagination]:my-4 [&_.ant-pagination]:font-['Montserrat'] [&_.ant-table-container]:border-none [&_.ant-table-content]:overflow-x-auto [&_.ant-empty-description]:font-['Montserrat'] [&_.ant-empty-description]:text-zinc-500">
        <CustomTable
          data={tableData}
          loading={loading}
          showPagination={false}
          handleTableChange={handleTableChange}
          columns={columns}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};

export default FundSubscriptionsTable;